/**
 * Relation rail state in Loro (spec §7.2, §7.5) and the pure edge model (§8.15).
 *
 * - Page document root map `view` → `rail` { enabled, tracks: LoroMovableList<LoroMap> }.
 * - Workspace document root map `railStyles` { styleId → plain RailStyle }.
 * Both are authored view configuration and never projected (§13.2).
 */
import { LoroMap, LoroMovableList, type LoroDoc, type TreeID } from "loro-crdt";
import { v7 as uuidv7 } from "uuid";
import type { OptionGroup } from "../links/groups";
import { readGroups } from "../links/groups";
import { readLinks } from "../links/model";
import { parseIri } from "../lib/iri";
import { groupLabel, type LayoutEdge, type LayoutGroup, type ResolvedStyle } from "./layout";

export type TrackSource = { kind: "predicate"; iri: string } | { kind: "query"; sparql: string };

export interface RailTrack {
  id: string;
  styleId: string;
  visible: boolean;
  label?: string;
  source: TrackSource;
}

export interface RailConfig {
  enabled: boolean;
  tracks: RailTrack[];
  /** [M24] workspace rail only: also draw links between the pages' blocks, aggregated (§8.25) */
  blockLinks?: boolean;
}

export type RailStyle = ResolvedStyle;

/** Labels are on by default (maintainer, 2026-10-16): a connector names its relation unless a style says otherwise. */
export const DEFAULT_STYLE: RailStyle = { label: "", stroke: "#7a8aa0", width: 2, dash: "solid", side: "left", arrow: "target", showLabel: true };

/** Colours offered to new styles, in order (distinguishable in both themes). */
export const PALETTE = ["#3b6fb6", "#2f8f5b", "#c0632a", "#8a4fb0", "#b33a5b", "#2a8c95", "#9a7b24", "#5b6b7a"];

// --- page view --------------------------------------------------------------------------------------

function railMap(doc: LoroDoc, create: boolean): LoroMap | null {
  const view = doc.getMap("view");
  const r = view.get("rail");
  if (r instanceof LoroMap) return r;
  if (!create) return null;
  const rail = view.setContainer("rail", new LoroMap());
  rail.set("enabled", false);
  rail.setContainer("tracks", new LoroMovableList());
  return rail;
}

function tracksList(rail: LoroMap): LoroMovableList {
  const t = rail.get("tracks");
  return t instanceof LoroMovableList ? t : rail.setContainer("tracks", new LoroMovableList());
}

export function readRail(doc: LoroDoc): RailConfig {
  const rail = railMap(doc, false);
  if (!rail) return { enabled: false, tracks: [] };
  const list = rail.get("tracks");
  const tracks: RailTrack[] = [];
  if (list instanceof LoroMovableList) {
    for (let i = 0; i < list.length; i++) {
      const m = list.get(i);
      if (!(m instanceof LoroMap)) continue;
      const source = m.get("source") as TrackSource | undefined;
      const id = m.get("id");
      if (typeof id !== "string" || !source || (source.kind !== "predicate" && source.kind !== "query")) continue;
      const label = m.get("label");
      tracks.push({
        id,
        styleId: String(m.get("styleId") ?? ""),
        visible: m.get("visible") !== false,
        ...(typeof label === "string" && label ? { label } : {}),
        source,
      });
    }
  }
  return { enabled: rail.get("enabled") === true, tracks, ...(rail.get("blockLinks") === true ? { blockLinks: true } : {}) };
}

/** [M24] Turn the aggregated block-link source of the workspace rail on or off (§8.25). */
export function setRailBlockLinks(doc: LoroDoc, on: boolean): void {
  railMap(doc, true)!.set("blockLinks", on);
  doc.commit();
}

export function setRailEnabled(doc: LoroDoc, enabled: boolean): void {
  railMap(doc, true)!.set("enabled", enabled);
  doc.commit();
}

export function addTrack(doc: LoroDoc, source: TrackSource, styleId: string, label?: string): string {
  const rail = railMap(doc, true)!;
  const list = tracksList(rail);
  const m = list.insertContainer(list.length, new LoroMap());
  const id = uuidv7();
  m.set("id", id);
  m.set("styleId", styleId);
  m.set("visible", true);
  if (label) m.set("label", label);
  m.set("source", source);
  doc.commit();
  return id;
}

function trackMap(doc: LoroDoc, trackId: string): { list: LoroMovableList; index: number; map: LoroMap } | null {
  const rail = railMap(doc, false);
  const list = rail?.get("tracks");
  if (!(list instanceof LoroMovableList)) return null;
  for (let i = 0; i < list.length; i++) {
    const m = list.get(i);
    if (m instanceof LoroMap && m.get("id") === trackId) return { list, index: i, map: m };
  }
  return null;
}

export function updateTrack(doc: LoroDoc, trackId: string, patch: Partial<Omit<RailTrack, "id">>): void {
  const t = trackMap(doc, trackId);
  if (!t) return;
  for (const [k, v] of Object.entries(patch)) {
    if (v === undefined) t.map.delete(k);
    else t.map.set(k, v as never);
  }
  doc.commit();
}

export function removeTrack(doc: LoroDoc, trackId: string): void {
  const t = trackMap(doc, trackId);
  if (!t) return;
  t.list.delete(t.index, 1);
  doc.commit();
}

// --- workspace styles ---------------------------------------------------------------------------------

export function readStyles(wsDoc: LoroDoc | null): Record<string, RailStyle> {
  if (!wsDoc) return {};
  const m = wsDoc.getMap("railStyles");
  const out: Record<string, RailStyle> = {};
  for (const id of m.keys()) {
    const v = m.get(id);
    if (v && typeof v === "object" && !(v instanceof LoroMap)) out[id] = { ...DEFAULT_STYLE, ...(v as Partial<RailStyle>) };
  }
  return out;
}

export function putStyle(wsDoc: LoroDoc, styleId: string, style: RailStyle): void {
  wsDoc.getMap("railStyles").set(styleId, { ...style });
  wsDoc.commit();
}

/** A new named style with the next unused palette colour. */
export function createStyle(wsDoc: LoroDoc, label: string, over: Partial<RailStyle> = {}): string {
  const existing = readStyles(wsDoc);
  const used = new Set(Object.values(existing).map((s) => s.stroke));
  const stroke = PALETTE.find((c) => !used.has(c)) ?? PALETTE[Object.keys(existing).length % PALETTE.length]!;
  const id = uuidv7();
  putStyle(wsDoc, id, { ...DEFAULT_STYLE, label, stroke, ...over });
  return id;
}

export function resolveStyle(styles: Record<string, RailStyle>, track: RailTrack): RailStyle {
  const s = styles[track.styleId] ?? DEFAULT_STYLE; // an unknown style falls back, never hides the track
  return { ...s, label: track.label || s.label };
}

// --- page relations ----------------------------------------------------------------------------------

export interface PageRelation {
  linkId: string;
  predicate: string;
  sourceTree: TreeID;
  /** block the target resolves to on this page (a checklist item resolves to its checklist), or null */
  targetTree: TreeID | null;
  /** [M24] an aggregate of this many block-level links between two pages: drawn dimmed, counted */
  count?: number;
}

export interface PredicateSummary {
  iri: string;
  onPage: number;
  offPage: number;
}

/** Every link from a block of this page to a block (§8.15 "page-internal links"). */
export function readPageRelations(doc: LoroDoc, docId: string, blockIds: TreeID[]): { relations: PageRelation[]; groups: Map<TreeID, OptionGroup[]> } {
  const relations: PageRelation[] = [];
  const groups = new Map<TreeID, OptionGroup[]>();
  for (const treeId of blockIds) {
    for (const link of readLinks(doc, docId, treeId)) {
      // an `iri` object naming a block counts too: links typed into the IRI tab before the dialog
      // normalised them were stored that way, and they project to the same triple
      if (link.o.kind !== "block" && link.o.kind !== "iri") continue;
      const target = parseIri(link.o.value);
      if (!target?.doc || !target.treeId || (link.o.kind === "iri" && target.kind !== "block" && target.kind !== "checklist_item")) continue;
      relations.push({ linkId: link.id, predicate: link.p, sourceTree: treeId, targetTree: target.doc === docId ? (target.treeId as TreeID) : null });
    }
    const g = readGroups(doc, treeId);
    if (g.length) groups.set(treeId, g);
  }
  return { relations, groups };
}

export function summarisePredicates(relations: PageRelation[]): PredicateSummary[] {
  const by = new Map<string, PredicateSummary>();
  for (const r of relations) {
    const s = by.get(r.predicate) ?? { iri: r.predicate, onPage: 0, offPage: 0 };
    if (r.targetTree) s.onPage++;
    else s.offPage++;
    by.set(r.predicate, s);
  }
  return [...by.values()].sort((a, b) => b.onPage + b.offPage - (a.onPage + a.offPage) || a.iri.localeCompare(b.iri));
}

/** Query-track rows: `?source ?target` block IRIs, optional `?label` (§15.4). */
export interface QueryEdge {
  source: string;
  target: string;
  label?: string;
}

export interface RowRef {
  treeId: TreeID;
  parent: TreeID | null;
}

/** Visible row index of a block, or of its nearest visible ancestor (a collapsed group). */
export function rowResolver(allRows: RowRef[], visibleRows: RowRef[]): (treeId: TreeID) => number | undefined {
  const visible = new Map(visibleRows.map((r, i) => [r.treeId, i]));
  const parent = new Map(allRows.map((r) => [r.treeId, r.parent]));
  return (treeId) => {
    for (let t: TreeID | null | undefined = treeId; t; t = parent.get(t)) {
      const i = visible.get(t);
      if (i !== undefined) return i;
    }
    return undefined;
  };
}

export interface EdgeInputs {
  docId: string;
  config: RailConfig;
  styles: Record<string, RailStyle>;
  relations: PageRelation[];
  groups: Map<TreeID, OptionGroup[]>;
  queryResults: Map<string, QueryEdge[]>;
  allRows: RowRef[];
  visibleRows: RowRef[];
  /** [M24] the workspace rail: a query row's endpoints are doc IRIs, resolved to page-tree rows */
  resolveIri?: (iri: string) => TreeID | undefined;
}

export interface RailEdges {
  edges: LayoutEdge[];
  groups: LayoutGroup[];
}

export function buildRailEdges(input: EdgeInputs): RailEdges {
  const { docId, config, styles } = input;
  if (!config.enabled) return { edges: [], groups: [] };
  const rowOf = rowResolver(input.allRows, input.visibleRows);
  const edges: LayoutEdge[] = [];
  const memberOf = new Map<string, OptionGroup>();
  for (const groups of input.groups.values()) for (const g of groups) for (const m of g.members) memberOf.set(m, g);
  const usedGroups = new Map<string, OptionGroup>();

  for (const track of config.tracks) {
    if (!track.visible) continue;
    const style = resolveStyle(styles, track);
    if (track.source.kind === "predicate") {
      const iri = track.source.iri;
      for (const r of input.relations) {
        if (r.predicate !== iri || !r.targetTree) continue;
        const from = rowOf(r.sourceTree);
        const to = rowOf(r.targetTree);
        if (from === undefined || to === undefined) continue;
        const group = memberOf.get(r.linkId);
        if (group) usedGroups.set(group.id, group);
        const dimmed = group && group.chosen.length > 0 && !group.chosen.includes(r.linkId);
        // [M24] an aggregate of block links between two pages (§8.25): dimmed, and counted
        const counted = r.count !== undefined ? { ...style, label: `${style.label || ""} ×${r.count}`.trim(), showLabel: true } : style;
        const opacity = dimmed ? 0.35 : r.count !== undefined ? 0.45 : 1;
        edges.push({ id: `${track.id}:${r.linkId}`, track: track.id, from, to, style: counted, opacity, ...(group ? { groupId: group.id } : {}) });
      }
    } else {
      (input.queryResults.get(track.id) ?? []).forEach((q, i) => {
        if (input.resolveIri) {
          const a = input.resolveIri(q.source);
          const b = input.resolveIri(q.target);
          const from = a ? rowOf(a) : undefined;
          const to = b ? rowOf(b) : undefined;
          if (from === undefined || to === undefined) return;
          edges.push({ id: `${track.id}:q${i}`, track: track.id, from, to, style: q.label ? { ...style, label: q.label } : style, opacity: 1 });
          return;
        }
        const s = parseIri(q.source);
        const t = parseIri(q.target);
        // rows whose endpoints are not both blocks of this page are dropped (§15.4)
        if (s?.doc !== docId || t?.doc !== docId || !s.treeId || !t.treeId) return;
        const from = rowOf(s.treeId as TreeID);
        const to = rowOf(t.treeId as TreeID);
        if (from === undefined || to === undefined) return;
        edges.push({ id: `${track.id}:q${i}`, track: track.id, from, to, style: q.label ? { ...style, label: q.label } : style, opacity: 1 });
      });
    }
  }
  const groups: LayoutGroup[] = [...usedGroups.values()].map((g) => ({ id: g.id, label: groupLabel(g.minChoice, g.maxChoice, g.members.length) }));
  return { edges, groups };
}

/** Rows of a SPARQL result in the rail convention. */
export function queryEdges(bindings: Array<Record<string, { value: string } | undefined>>): QueryEdge[] {
  return bindings
    .filter((b) => b.source?.value && b.target?.value)
    .map((b) => ({ source: b.source!.value, target: b.target!.value, ...(b.label?.value ? { label: b.label.value } : {}) }));
}
